import axios from '../lib/axios';
import { showToast } from '../utils/toast';

// Pre Approval Log interface
export interface PreApprovalLog {
  _id: string;
  platform: string;
  lender: string;
  preApprovalResponse: {
    amount?: number;
    status?: string;
    error?: string;
    applicationId?: number;
    applicantId?: number;
    approvedLoanAmount?: number | string;
  };
  remark?: string;
  customerName: string;
  customerMobileNo: string;
  createdAt: string;
  merchantName: string;
  merchantMobileNo: string;
  partnerName: string;
  serialNumber?: number; // Added by the frontend
}

// Pre Approval Log details response interface
export interface PreApprovalLogDetailResponse {
  success: boolean;
  data: {
    platform: string;
    gatewayPartnerName?: string;
    gatewayPartnerMobile?: string;
    merchantName?: string;
    merchantMobile?: string;
    customerName?: string;
    customerMobile?: string;
    customerMobileNo?: string,
    customerEmail?: string,
    customerPan?: string,
    customerdob?: string,
    customerEmploymentType?: number,
    customerMonthlyIncome?: number,
    customerCreatedAt?: string,
    partnerName?: string;
    note?: string;
    lender?: string;
    status?: string;
    amount?: number;
    createdAt?: string;
  };
}

// API response interface
export interface PreApprovalLogApiResponse {
  status: string;
  result: {
    data: {
      totalCount: number;
      totalMatch: number;
      totalPages: number;
      pageNo: number;
      pageSize: number;
      results: PreApprovalLog[];
    }
  }
}

// Filter type
export interface PreApprovalLogFilters {
  lender?: string;
  platform?: string;
  partner?: string;
  dateRange?: {
    from?: string;
    to?: string;
  };
}

// Helper function to format date for API
const formatDateForAPI = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
};

// Fetch parameters interface
export interface FetchPreApprovalLogsParams {
  page: number;
  pageSize: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  token?: string | null;
  filters?: PreApprovalLogFilters;
}

/**
 * Fetch pre-approval logs with pagination, sorting, and search
 */
export const fetchPreApprovalLogs = async ({
  page,
  pageSize,
  sortField,
  sortOrder,
  search,
  token,
  filters = {}
}: FetchPreApprovalLogsParams): Promise<{
  logs: PreApprovalLog[];
  totalCount: number;
  totalMatch: number;
  totalPages: number;
  pageNo: number;
  pageSize: number;
}> => {
  try {
    // Get token from localStorage if not provided
    let authToken = token;
    if (!authToken) {
      authToken = localStorage.getItem('token') || '';
    }

    // Check if token exists
    if (!authToken) {
      throw new Error("No authentication token available");
    }

    // Build query parameters
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("pageSize", String(pageSize));

    // Add sort parameters if provided
    if (sortField && sortOrder) {
      params.append("sortBy", sortField);
      params.append("sortOrder", sortOrder);
    }

    // Add search parameter if provided
    if (search) {
      params.append("searchQuery", search);
    }

    // Add filter parameters
    if (filters.platform) {
      params.append("platform", filters.platform);
    }

    if (filters.lender) {
      params.append("lender", filters.lender);
    }

    if (filters.partner) {
      params.append("partner", filters.partner);
    }

    // Date filters use 'from' and 'to' params in DD-MM-YYYY format
    if (filters.dateRange?.from) {
      params.append("from", formatDateForAPI(filters.dateRange.from));
    }
    if (filters.dateRange?.to) {
      params.append("to", formatDateForAPI(filters.dateRange.to));
    }

    const url = `/adminpreapproval/logs?${params.toString()}`;
    console.log(`[API] Fetching pre-approval logs with URL: ${url}`);

    const response = await axios.get<PreApprovalLogApiResponse>(url, {
      headers: {
        "Content-Type": "application/json",
        token: authToken,
      },
    });

    // Check if response is valid
    if (!response.data?.result?.data?.results) {
      throw new Error("Unexpected API response structure");
    }

    // Add serialNumber to each log entry
    const logs = response.data.result.data.results.map((log, index) => ({
      ...log,
      serialNumber: (page - 1) * pageSize + index + 1
    }));

    return {
      logs,
      totalCount: response.data.result.data.totalCount,
      totalMatch: response.data.result.data.totalMatch,
      totalPages: response.data.result.data.totalPages,
      pageNo: response.data.result.data.pageNo,
      pageSize: response.data.result.data.pageSize
    };
  } catch (error: any) {
    console.error('Error fetching pre-approval logs:', error);
    showToast.error(error.message || 'Failed to fetch pre-approval logs');
    throw error;
  }
};

/**
 * Fetch pre-approval log details by ID
 */
export const fetchPreApprovalLogDetails = async (
  id: string,
  token?: string | null
): Promise<PreApprovalLogDetailResponse> => {
  try {
    // Get token from localStorage if not provided
    let authToken = token;
    if (!authToken) {
      authToken = localStorage.getItem('token') || '';
    }

    // Check if token exists
    if (!authToken) {
      throw new Error("No authentication token available");
    }

    // Check if ID is provided
    if (!id) {
      throw new Error("ID is required");
    }

    const url = `/adminpreapproval/details?id=${id}`;
    console.log(`[API] Fetching pre-approval log details with URL: ${url}`);

    const response = await axios.get<PreApprovalLogDetailResponse>(url, {
      headers: {
        "Content-Type": "application/json",
        token: authToken,
      },
    });

    // Check if response is valid
    if (!response.data || !response.data.success) {
      throw new Error("Unexpected API response structure");
    }

    return response.data;
  } catch (error: any) {
    console.error('Error fetching pre-approval log details:', error);
    showToast.error(error.message || 'Failed to fetch pre-approval log details');
    throw error;
  }
}; 